/** Bộ 7 bước CSKH — gương của `cskh.PipelineStages` bên Go.
 *
 *  Đây là thứ tự HIỂN THỊ (thanh bước, bộ đếm tab), KHÔNG phải luật đi đường.
 *  Quy trình có vòng lặp fix ảnh nên đường đi nằm ở `PIPELINE_EDGES`. */
export const PIPELINE_STAGES = [
  "intake",
  "waiting_selection",
  "retouching",
  "final_ready",
  "final_sent",
  "fixing",
  "done",
] as const;

export type PipelineStage = (typeof PIPELINE_STAGES)[number];

const LABELS: Record<string, string> = {
  intake: "Nhập, sửa ảnh",
  waiting_selection: "Chờ khách chọn ảnh",
  retouching: "Retouch",
  final_ready: "Trả ảnh hoàn thiện",
  // Trước v3 khoá này mang nhãn "Trả final" — gộp hai việc khác nhau, hai người
  // khác nhau xử. Nay "trả final" là bước ④; ⑤ là lúc bóng đã sang sân khách.
  final_sent: "Chờ khách xác nhận",
  fixing: "Retouch fix",
  done: "Hoàn tất",
};

/** Bước CSKH của ERP cũ ↦ bước chuẩn v3 — gương của `cskh.LegacyStageMap`
 *  bên Go và của `migrations/0196_cskh_pipeline_stage_vocab.up.sql`.
 *
 *  Lưới an toàn khi dữ liệu chưa chạy bản vá: đo 01/09/2026 trên cơ sở dữ liệu
 *  thật, 975/1024 hồ sơ vẫn mang `new_box` vì cả bảng được nạp lại SAU khi bản
 *  vá 0039 chạy. Không có bảng này thì màn CSKH hiện thẳng khoá tiếng Anh.
 *
 *  Ánh xạ NHIỀU-VỀ-MỘT, cố ý mất nghĩa y như bản vá — không đảo ngược được. */
export const LEGACY_STAGE_MAP: Record<string, string> = {
  new_box: "intake",
  greeted: "intake",
  wait_raw: "intake",
  box_sent: "waiting_selection",
  selecting: "waiting_selection",
  raw_sent: "waiting_selection",
  wait_select: "waiting_selection",
  confirmed: "retouching",
  wait_final: "retouching",
  selected: "retouching",
  wait_ps_pay: "retouching",
  review: "retouching",
  print_wait: "final_sent",
  printing: "final_sent",
  approved: "final_sent",
  upsale_print: "final_sent",
  fix_request: "fixing",
  aftercare: "done",
};

/** Đưa khoá bước bất kỳ về bộ 7 bước chuẩn. Khoá lạ hoàn toàn trả nguyên —
 *  nuốt thành `intake` là bịa ra trạng thái vận hành chưa từng có. */
export function normalizeStage(key: string): string {
  return LEGACY_STAGE_MAP[key] ?? key;
}

export function stageLabel(key: string): string {
  return LABELS[normalizeStage(key)] ?? key;
}

export function stageIndex(status: string): number {
  return PIPELINE_STAGES.indexOf(normalizeStage(status) as PipelineStage);
}

/** Bảng cạnh — gương của `pipelineEdges` bên Go (service.go). Server vẫn là
 *  nơi chốt; bảng này để giao diện biết vẽ nút nào.
 *
 *  Cố ý KHÔNG có: retouching → final_sent (mọi lần gửi khách phải qua ④),
 *  final_ready → done (chưa gửi thì không thể "khách OK"),
 *  fixing → final_sent (vòng fix phải qua ④ để đo cùng một cách). */
const PIPELINE_EDGES: Record<string, readonly string[]> = {
  intake: ["waiting_selection"],
  waiting_selection: ["retouching", "intake"],
  retouching: ["final_ready", "waiting_selection"],
  final_ready: ["final_sent", "retouching"],
  final_sent: ["done", "fixing", "final_ready"],
  fixing: ["final_ready"],
  done: ["fixing"],
};

/** Các bước đi tiếp được từ `current`.
 *
 *  Thay `allowedNext`/`allowedPrev` cũ: hai hàm đó diễn tả luật "nhích ±1"
 *  đã bỏ ở v3, và một quy trình có vòng lặp thì không có khái niệm "bước kế
 *  tiếp" duy nhất — ở `final_sent` có tới ba lối ra.
 *
 *  Bước ngoài bộ chuẩn (dữ liệu di trú): trả về mọi bước chuẩn TRỪ `done`,
 *  khớp nhánh legacy của server. Đóng sổ chỉ đi qua cửa xác nhận hộ, mà cửa
 *  đó chỉ mở ở bước ảnh cuối — sổ cũ phải về "Chờ khách xác nhận" trước. */
export function allowedTargets(current: string): readonly string[] {
  const edges = PIPELINE_EDGES[current];
  if (edges) return edges;
  return PIPELINE_STAGES.filter((s) => s !== current && s !== "done");
}

/** Nhãn bước ④ có kèm số vòng fix. CHỈ dùng ở màn chi tiết một đơn — thanh
 *  bước và bộ đếm dùng `stageLabel` (nhãn tĩnh) vì chúng gộp nhiều đơn.
 *
 *  Tham số là `revisions.length`, CỐ Ý không phải `pipeline.revisionCount`:
 *  migrator Appwrite ghi `revisionCount` thẳng từ blob nguồn nên đơn di trú có
 *  thể mang `revisionCount = 3` với `revisions = []`. */
export function finalReadyLabel(revisionsLength: number): string {
  return revisionsLength > 0
    ? `Trả link fix lần ${revisionsLength + 1}`
    : "Trả ảnh hoàn thiện";
}
